import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { 
  StarIcon,
  XMarkIcon,
  CheckCircleIcon,
  SparklesIcon,
  LockClosedIcon
} from '@heroicons/react/24/solid';
import { StarIcon as StarIconOutline } from '@heroicons/react/24/outline';
import { useToast } from './ToastProvider';

const ModalReview = ({ onClose, score = null }) => {
  const token = useSelector((state) => state.auth.token);
  const [comment, setComment] = useState('');
  const [rating, setRating] = useState(5);
  const [hoveredRating, setHoveredRating] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [authError, setAuthError] = useState(false);
  const toast = useToast();

  const isLoggedIn = !!token;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isLoggedIn) return;
    setIsSubmitting(true);
    setAuthError(false);

    try {
      // Si le commentaire est vide, envoyer une valeur par défaut
      const finalComment = comment && comment.trim() !== '' ? comment.trim() : 'Aucun commentaire';

      const response = await fetch('https://cvanalyzerback.osc-fr1.scalingo.io/api/reviews', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ comment: finalComment, rating }),
      });

      if (response.status === 401) {
        setAuthError(true);
        return;
      }

      if (!response.ok) {
        throw new Error('Erreur lors de la soumission');
      }

      setIsSuccess(true);
      
      // Enregistrer la date de l'avis pour ne pas redemander avant 30 jours
      localStorage.setItem('lastReviewDate', Date.now().toString());
      
      // Fermer automatiquement après 2 secondes
      setTimeout(() => {
        onClose();
      }, 2000);
    } catch (error) {
      console.error('Erreur lors de la création de l\'avis :', error);
      toast.error(
        'Envoi impossible',
        'Une erreur est survenue. Veuillez réessayer dans un instant.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const getRatingLabel = (ratingValue) => {
    const labels = {
      5: 'Excellent',
      4: 'Très bien',
      3: 'Bien',
      2: 'Moyen',
      1: 'Mauvais'
    };
    return labels[ratingValue] || '';
  };

  if (isSuccess) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 animate-fadeIn">
        <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full p-8 text-center transform transition-all duration-300">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6 animate-bounce">
            <CheckCircleIcon className="w-12 h-12 text-green-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-4">Merci pour votre avis !</h2>
          <p className="text-gray-600 mb-6">
            Votre avis nous aide à améliorer CV Analyse pour tous les utilisateurs.
          </p>
          <div className="flex items-center justify-center gap-2 text-yellow-500">
            {[...Array(5)].map((_, i) => (
              <StarIcon key={i} className="w-6 h-6" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Utilisateur non connecté : afficher le message explicite
  if (!isLoggedIn) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 animate-fadeIn">
        <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full p-8 transform transition-all duration-300">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center">
                <LockClosedIcon className="w-6 h-6 text-amber-600" />
              </div>
              <h2 className="text-xl font-bold text-gray-800">Laisser un avis</h2>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              aria-label="Fermer"
            >
              <XMarkIcon className="w-6 h-6 text-gray-600" />
            </button>
          </div>
          <p className="text-gray-700 mb-6">
            <strong>Vous devez être connecté pour laisser un avis.</strong> Connectez-vous à votre compte ou créez-en un pour partager votre expérience avec CV Analyse.
          </p>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-6 py-3 bg-gray-100 text-gray-700 font-semibold rounded-xl hover:bg-gray-200 transition-all duration-200"
            >
              Fermer
            </button>
            <Link
              to="/login"
              onClick={onClose}
              className="flex-1 px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-semibold rounded-xl hover:from-blue-700 hover:to-purple-700 text-center shadow-lg transition-all duration-200"
            >
              Se connecter
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 animate-fadeIn">
      <div className="bg-white rounded-3xl shadow-2xl max-w-lg w-full transform transition-all duration-300">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-t-3xl p-6 text-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                <SparklesIcon className="w-6 h-6" />
              </div>
              <div>
                <h2 className="text-2xl font-bold">Votre avis nous intéresse !</h2>
                <p className="text-blue-100 text-sm mt-1">
                  {score !== null ? `Vous avez obtenu ${score}/100` : 'Partagez votre expérience'}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/20 rounded-full transition-colors"
              aria-label="Fermer"
            >
              <XMarkIcon className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="p-6">
          {authError && (
            <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-xl flex items-start gap-3">
              <LockClosedIcon className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-amber-800">
                <p className="font-semibold">Vous devez être connecté pour laisser un avis.</p>
                <p className="text-amber-700 mt-1">Veuillez vous connecter puis réessayer.</p>
              </div>
            </div>
          )}
          {/* Rating Section */}
          <div className="mb-6">
            <label className="block text-sm font-semibold text-gray-700 mb-3">
              Comment évaluez-vous cette analyse ?
            </label>
            <div className="flex items-center gap-2 mb-2">
              {[1, 2, 3, 4, 5].map((star) => {
                const isFilled = star <= (hoveredRating || rating);
                return (
                  <button
                    key={star}
                    type="button"
                    onClick={() => setRating(star)}
                    onMouseEnter={() => setHoveredRating(star)}
                    onMouseLeave={() => setHoveredRating(0)}
                    className="transition-transform hover:scale-125 focus:outline-none"
                    aria-label={`Noter ${star} étoile${star > 1 ? 's' : ''}`}
                  >
                    {isFilled ? (
                      <StarIcon className="w-10 h-10 text-yellow-500 transition-colors" />
                    ) : (
                      <StarIconOutline className="w-10 h-10 text-gray-300 transition-colors" />
                    )}
                  </button>
                );
              })}
            </div>
            <p className="text-sm font-medium text-gray-600">
              {getRatingLabel(hoveredRating || rating)}
            </p>
          </div>

          {/* Comment Section */}
          <div className="mb-6">
            <label htmlFor="comment" className="block text-sm font-semibold text-gray-700 mb-3">
              Votre commentaire (optionnel)
            </label>
            <textarea
              id="comment"
              value={comment}
              onChange={(e) => {
                if (e.target.value.length <= 500) {
                  setComment(e.target.value);
                }
              }}
              placeholder="Partagez votre expérience... Cette analyse vous a-t-elle aidé ? Avez-vous des suggestions ?"
              rows={4}
              maxLength={500}
              className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 resize-none"
            />
            <p className={`text-xs mt-2 ${
              comment.length > 450 
                ? 'text-red-500 font-semibold' 
                : comment.length > 400 
                ? 'text-orange-500' 
                : 'text-gray-500'
            }`}>
              {comment.length}/500 caractères
            </p>
          </div>

          {/* Info Box */}
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
            <div className="flex items-start gap-3">
              <SparklesIcon className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-blue-800">
                <p className="font-medium mb-1">Pourquoi votre avis compte</p>
                <p className="text-blue-700">
                  Vos retours nous aident à améliorer CV Analyse et à aider d'autres candidats à décrocher leur emploi de rêve.
                </p>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-6 py-3 bg-gray-100 text-gray-700 font-semibold rounded-xl hover:bg-gray-200 transition-all duration-200"
            >
              Passer
            </button>
            <button
              type="submit"
              disabled={isSubmitting || rating === 0}
              className={`flex-1 px-6 py-3 font-semibold rounded-xl transition-all duration-200 ${
                isSubmitting || rating === 0
                  ? 'bg-gray-400 cursor-not-allowed text-white'
                  : 'bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white shadow-lg hover:shadow-xl transform hover:-translate-y-0.5'
              }`}
            >
              {isSubmitting ? (
                <div className="flex items-center justify-center gap-2">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  <span>Envoi...</span>
                </div>
              ) : (
                <div className="flex items-center justify-center gap-2">
                  <StarIcon className="w-5 h-5" />
                  <span>Envoyer mon avis</span>
                </div>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ModalReview;
